import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass, Plus, Users } from 'lucide-react';
import { WaitlistRow, WaitlistStatus, getWaitlist, listClasses, ClassRow } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { WaitlistActions, STATUS_META, daysWaiting, AddForm } from '../components/WaitlistPanel';
import EmptyState from '../../../components/EmptyState';
import { colorClase } from '../../../utils/classColor';

const FILTERS: { key: WaitlistStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'waiting', label: 'En espera' },
  { key: 'contacted', label: 'Contactados' },
  { key: 'enrolled', label: 'Inscritos' },
  { key: 'discarded', label: 'Descartados' },
];

export default function ProspectsPage() {
  const [rows, setRows] = useState<WaitlistRow[]>([]);
  const [filter, setFilter] = useState<WaitlistStatus | 'all'>('all');
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [addClassId, setAddClassId] = useState<number | ''>('');
  const load = useCallback(() => {
    getWaitlist().then((r) => { setRows(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!adding || classes.length) return;
    listClasses().then((r) => setClasses(r.data.filter((c) => c.status === 'active'))).catch(() => {});
  }, [adding, classes.length]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter]);
  const waiting = rows.filter((r) => r.status === 'waiting').length;
  const contacted = rows.filter((r) => r.status === 'contacted').length;

  // Agrupado por clase, la de espera más vieja primero (a quién hablarle si se abre lugar).
  const groups = useMemo(() => {
    const map = new Map<number, WaitlistRow[]>();
    for (const r of visible) {
      if (!map.has(r.class_id)) map.set(r.class_id, []);
      map.get(r.class_id)!.push(r);
    }
    return [...map.values()].sort((a, b) => (a[0].start_date || '9999').localeCompare(b[0].start_date || '9999'));
  }, [visible]);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content"><Hourglass className="text-brand-blue" size={24} /> Prospectos</h2>
          <p className="mt-1 text-sm text-content-2">
            Personas en lista de espera por clase. {waiting > 0 && <b>{waiting} esperando</b>}{contacted > 0 && <> · {contacted} contactadas</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`rounded-full px-3 py-1 text-xs transition ${filter === f.key ? 'bg-accent font-semibold text-on-accent shadow-sm' : 'border border-line font-medium text-content-2 hover:bg-surface-2'}`}>
              {f.label}
            </button>
          ))}
          {!adding && (
            <button onClick={() => setAdding(true)}
              className="ml-1 flex items-center gap-1 rounded-lg bg-accent-solid px-3 py-1.5 text-xs font-semibold text-on-accent transition hover:opacity-90">
              <Plus size={14} /> Agregar prospecto
            </button>
          )}
        </div>
      </div>

      {adding && (
        <div className="mt-4 rounded-2xl border border-line bg-surface p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-content">Nuevo prospecto</h3>
            <button onClick={() => { setAdding(false); setAddClassId(''); }} className="text-xs text-content-3 hover:text-content">Cerrar</button>
          </div>
          <select value={addClassId} onChange={(e) => setAddClassId(e.target.value ? Number(e.target.value) : '')}
            className="mt-2 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-accent sm:max-w-sm">
            <option value="">¿Para qué clase pregunta?</option>
            {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
          </select>
          {addClassId !== '' && (
            <AddForm classId={addClassId} onDone={() => { setAdding(false); setAddClassId(''); load(); }}
              onCancel={() => { setAdding(false); setAddClassId(''); }} />
          )}
        </div>
      )}

      {loaded && rows.length === 0 && (
        <div className="mt-8">
          <EmptyState icon={Hourglass} title="Sin prospectos por ahora"
            subtitle="Cuando alguien pregunte por una clase llena (o quiera regresar), agrégala desde la ficha de la clase y aquí verás a todos juntos." />
        </div>
      )}

      <div className="mt-5 space-y-5">
        {groups.map((g) => (
          <div key={g[0].class_id} className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
            <Link to={`/clases/${g[0].class_id}`}
              className="flex items-center gap-2 border-b border-line px-4 py-2.5 transition hover:bg-surface-2">
              <span className="h-3 w-3 rounded" style={{ background: colorClase(g[0].class_color) }} />
              <span className="font-medium text-content">{g[0].class_name}</span>
              <span className="ml-auto flex items-center gap-1 text-xs text-content-3">
                <Users size={13} /> {g[0].enrolled}{g[0].capacity ? `/${g[0].capacity}` : ''} inscritos
              </span>
            </Link>
            <div className="divide-y divide-line">
              {g.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                  <PersonAvatar contactId={r.contact_id} hasPhoto={r.has_photo}
                    name={r.first_name} lastName={r.last_name} size="h-8 w-8" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <Link to={`/alumnos/${r.contact_id}`} className="font-medium text-content hover:underline">{r.first_name} {r.last_name || ''}</Link>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_META[r.status].cls}`}>{STATUS_META[r.status].label}</span>
                      {(r.status === 'waiting' || r.status === 'contacted') && (
                        <span className="text-xs text-content-3">esperando {daysWaiting(r.start_date)} días</span>
                      )}
                    </div>
                    {r.note && <p className="mt-0.5 truncate text-xs text-content-3">{r.note}</p>}
                  </div>
                  <WaitlistActions row={r} onChanged={load} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
